import React, { createContext, useState, useEffect } from 'react';
import { useBusinessAccounts, BusinessAccount } from '@/hooks/useBusinessAccounts';

export interface BusinessAccountContextType {
  selectedAccountId: string | null;
  selectedAccount: BusinessAccount | null;
  setSelectedAccountId: (accountId: string) => void;
  clearSelectedAccount: () => void;
  accounts: BusinessAccount[] | null;
  isLoading: boolean;
}

// Trust whatever id shape the backend gives us. The previous strict UUID
// regex silently dropped legitimate ids that didn't match RFC 4122 hex
// (e.g. ids ending in non-hex chars), causing refresh to lose the user's
// selection and revert to accounts[0]. Validation against the actual
// accounts list happens implicitly via `selectedAccount = find(...) || null`.
const isValidStoredId = (v: string | null) => typeof v === 'string' && v.trim().length > 0;

const globalKey = '__BUSINESS_ACCOUNT_CONTEXT__';
const globalObject = typeof globalThis !== 'undefined' ? (globalThis as any) : {};

export const BusinessAccountContext: React.Context<BusinessAccountContextType | undefined> =
  globalObject[globalKey] || createContext<BusinessAccountContextType | undefined>(undefined);

if (globalObject && !globalObject[globalKey]) {
  globalObject[globalKey] = BusinessAccountContext;
}

export const BusinessAccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: accounts, isLoading } = useBusinessAccounts();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('selectedBusinessAccountId');
      if (isValidStoredId(stored)) return stored;
    }
    return null;
  });

  useEffect(() => {
    if (selectedAccountId && typeof window !== 'undefined') {
      localStorage.setItem('selectedBusinessAccountId', selectedAccountId);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    if (accounts && accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId, setSelectedAccountId]);

  // Debug logging
  useEffect(() => {
  }, [accounts, selectedAccountId]);

  const selectedAccount = accounts?.find(account => account.id === selectedAccountId) || null;

  const clearSelectedAccount = () => {
    setSelectedAccountId(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('selectedBusinessAccountId');
    }
  };

  const value: BusinessAccountContextType = {
    selectedAccountId,
    selectedAccount,
    setSelectedAccountId,
    clearSelectedAccount,
    accounts: accounts || null,
    isLoading,
  };

  return (
    <BusinessAccountContext.Provider value={value}>
      {children}
    </BusinessAccountContext.Provider>
  );
};

export default BusinessAccountProvider;
